// WHAT: Recent Activity Component (Organism)
// WHY:  Displays last session information with motivational messages and quick actions
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: UI Card, Button, Stack, Heading, Badge components, Lucide icons
// RISKS:
//   - NOTE: Date formatting uses simple day calculation (may be inaccurate across timezones)
//   - NOTE: Motivational messages are hardcoded (not personalized)
//   - NOTE: Callbacks are optional (may cause errors if not provided)

import { Clock, Play, History, Sparkles } from "lucide-react";
import React from "react";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

interface RecentActivityProperties {
  className?: string;
  hasActiveSession?: boolean;
  lastSessionDate?: string;
  lastSessionType?: "Evening" | "Morning";
  onResumeSession?: () => void;
  onStartSession?: () => void;
  onViewHistory?: () => void;
}

/**
 * Formats last session date as relative time string.
 *
 * @param dateString - ISO date string of last session
 *
 * @returns Formatted date string (Today, Yesterday, X days ago, or "No sessions yet")
 *
 */
const formatLastSessionDate = (dateString?: string): string => {
  if (!dateString) {
    return "No sessions yet";
  }

  try {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Yesterday";
    }
    if (diffDays === 0) {
      return "Today";
    }
    return `${String(diffDays)} days ago`;
  } catch {
    return "Unknown";
  }
};

/**
 * WHAT:
 *   Organism component displaying recent session activity with quick actions.
 *
 * WHY:
 *   Provides context about last session and easy access to start/resume sessions.
 *
 * PROPS:
 *   - lastSessionDate?: string — ISO date string of last session
 *   - lastSessionType?: 'Morning' | 'Evening' — Type of last session
 *   - hasActiveSession?: boolean — Whether session is currently active
 *   - onStartSession?: () => void — Callback to start new session
 *   - onResumeSession?: () => void — Callback to resume active session
 *   - onViewHistory?: () => void — Callback to view session history
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Formats last session date as relative time (Today, Yesterday, X days ago)
 *   - Shows empty state if no last session date
 *   - Displays motivational message based on days since last session
 *   - Shows resume button if hasActiveSession, start button otherwise
 *   - Displays session type badge with color coding (blue=Morning, orange=Evening)
 *   - Provides view history button
 *
 * RISKS:
 *   - NOTE: Date calculation uses Math.ceil which may cause off-by-one errors
 *   - NOTE: Timezone differences may cause incorrect "Today" detection
 *   - NOTE: Callbacks are optional - buttons may not work if not provided
 *   - NOTE: Motivational messages are generic (not personalized to user)
 *
 * @param root0
 *
 * @param root0.lastSessionDate
 *
 * @param root0.lastSessionType
 *
 * @param root0.hasActiveSession
 *
 * @param root0.onStartSession
 *
 * @param root0.onResumeSession
 *
 * @param root0.onViewHistory
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const RecentActivity: React.FC<RecentActivityProperties> = ({
  className,
  hasActiveSession,
  lastSessionDate,
  lastSessionType,
  onResumeSession,
  onStartSession,
  onViewHistory,
}) => {
  const getMotivationalMessage = () => {
    if (!lastSessionDate) {
      return "Start your journey today and build your first streak!";
    }

    const date = new Date(lastSessionDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Great job today! Keep the momentum going!";
    } else if (diffDays === 1) {
      return "You're doing great! Continue your routine today.";
    } else if (diffDays <= 3) {
      return "You're on track! Time for your next session.";
    } else {
      return "Ready to get back on track? Start a session now!";
    }
  };

  return (
    <Card
      className={cn(
        "bg-[#1E293B] border-[#334155]",
        "hover:border-tracq-bright-blue/50 transition-all duration-300",
        "hover:shadow-tracq-card",
        className,
      )}
      padding="lg"
      variant="default"
    >
      <Stack direction="col" gap={4}>
        <Stack align="center" direction="row" gap={2}>
          <Clock className="w-5 h-5 text-tracq-bright-blue" />
          <Heading className="text-[#F1F5F9] font-semibold" level="h3" size="md">
            Recent Activity
          </Heading>
        </Stack>

        {/* Last Session Info */}
        {lastSessionDate ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#0F172A] rounded-tracq border border-[#334155]">
              <Stack align="center" direction="row" gap={3}>
                <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <Stack direction="col" gap={1}>
                  <div className="text-sm text-[#F1F5F9] font-medium">
                    Last Session: {lastSessionType ?? "Session"}
                  </div>
                  <div className="text-xs text-[#94A3B8]">
                    {formatLastSessionDate(lastSessionDate)}
                  </div>
                </Stack>
              </Stack>
              {lastSessionType ? (
                <Badge
                  className={cn(
                    lastSessionType === "Morning"
                      ? "bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                      : "bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30",
                  )}
                  size="sm"
                  variant="info"
                >
                  {lastSessionType}
                </Badge>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="p-4 bg-[#0F172A] rounded-tracq border border-[#334155] text-center">
            <p className="text-[#94A3B8] text-sm">No sessions completed yet</p>
          </div>
        )}

        {/* Motivational Message */}
        <div className="p-3 bg-gradient-to-r from-tracq-deep-blue/10 to-tracq-bright-blue/10 rounded-tracq border border-tracq-bright-blue/20">
          <p className="text-sm text-[#CBD5E1] text-center">{getMotivationalMessage()}</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {hasActiveSession ? (
            <Button
              className="w-full bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold"
              onClick={onResumeSession}
              size="md"
              variant="primary"
            >
              <Play className="w-4 h-4 mr-2" />
              Resume Session
            </Button>
          ) : (
            <Button
              className="w-full bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold"
              onClick={onStartSession}
              size="md"
              variant="primary"
            >
              <Play className="w-4 h-4 mr-2" />
              Start New Session
            </Button>
          )}

          <Button
            className="w-full border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
            onClick={onViewHistory}
            size="md"
            variant="outline"
          >
            <History className="w-4 h-4 mr-2" />
            View History
          </Button>
        </div>
      </Stack>
    </Card>
  );
};
